import { onMounted, reactive } from 'vue';
import { useToast } from 'primevue/usetoast';
import { deleteFilterPreset, fetchFilterPresets, saveFilterPreset } from '@/api/presetsApi';

export function useFilterPresets(scope, { getFilters, applyFilters }) {
  const toast = useToast();

  const filterPresets = reactive({
    presets: [],
    selectedPresetId: null,
    savingPreset: false,
    loadingPresets: false,

    async loadPresets() {
      filterPresets.loadingPresets = true;
      try {
        filterPresets.presets = await fetchFilterPresets(scope);
      } catch (err) {
        toast.add({ severity: 'error', summary: 'Presets', detail: err.message, life: 4000 });
      } finally {
        filterPresets.loadingPresets = false;
      }
    },

    applyPreset(presetId) {
      filterPresets.selectedPresetId = presetId || null;
      if (!presetId) return;
      const preset = filterPresets.presets.find((p) => p.presetId === presetId);
      if (preset?.filters && typeof applyFilters === 'function') {
        applyFilters(preset.filters);
      }
    },

    async saveCurrentPreset(name) {
      const trimmed = String(name || '').trim();
      if (!trimmed) {
        toast.add({ severity: 'warn', summary: 'Preset name required', life: 3000 });
        return;
      }
      filterPresets.savingPreset = true;
      try {
        const filters = typeof getFilters === 'function' ? getFilters() : {};
        const saved = await saveFilterPreset({
          scope,
          name: trimmed,
          filters,
          presetId: filterPresets.selectedPresetId || undefined
        });
        await filterPresets.loadPresets();
        filterPresets.selectedPresetId = saved.presetId || null;
        toast.add({ severity: 'success', summary: 'Preset saved', life: 2500 });
      } catch (err) {
        toast.add({ severity: 'error', summary: 'Save preset failed', detail: err.message, life: 4000 });
      } finally {
        filterPresets.savingPreset = false;
      }
    },

    async removePreset(presetId) {
      if (!presetId) return;
      try {
        await deleteFilterPreset(presetId);
        if (filterPresets.selectedPresetId === presetId) {
          filterPresets.selectedPresetId = null;
        }
        await filterPresets.loadPresets();
        toast.add({ severity: 'info', summary: 'Preset deleted', life: 2500 });
      } catch (err) {
        toast.add({ severity: 'error', summary: 'Delete preset failed', detail: err.message, life: 4000 });
      }
    }
  });

  onMounted(() => {
    filterPresets.loadPresets();
  });

  return filterPresets;
}
